<template>
    <main
        id="page-notepad"
        class="bg-gray-100"
    >
        <div class="container mx-auto">
            <Breadcrumb title="Mein Merkzettel"/>
        </div>
        <section class="container mx-auto pt-8 pb-16 space-y-4 lg:pt-16">
            <h1 class="text-2xl! lg:text-5xl! text-secondary! font-normal!">
                Mein Merkzettel
            </h1>
            <p>
                Noch nicht fertig mit der Auswahl? Wenn Sie sich angemeldet haben, bleibt Ihr Merkzettel erhalten: Ihre
                Auswahl wird beim Verlassen der Seite nicht gelöscht und steht Ihnen beim nächsten Besuch wieder zur
                Verfügung. Sie können dann weitere Geräte hinzufügen oder entfernen!
            </p>
            <p>
                Zusätzlich bietet der Merkzettel die Möglichkeit, ein unverbindliches Angebot anzufragen, das alle
                relevanten Daten zu Artikel, Menge, Preis und Gesamtkosten enthält. Optional können Sie eine Montage
                oder Lieferung anfragen.
            </p>
        </section>

        <section class="pb-16 lg:bg-white lg:pt-16">
            <div class="container mx-auto space-y-4">
                <div
                    v-for="item in items"
                    :key="item.id"
                    class="product-item"
                >
                    <div
                        class="absolute top-2 right-2 cursor-pointer"
                        @click="removeItem(item)"
                    >
                        <IconClose :tw-class="'w-8 h-8 fill-neutral-500'"/>
                    </div>
                    <div>
                        <div class="col-left">
                            <div>
                                <h2 class="normal-case! text-neutral-700!">
                                    {{ item.article.name }}
                                </h2>
                                <p>{{ item.article.articleNumber }}</p>
                            </div>
                            <div>
                                <p>
                                    {{ item.article.name2 }}
                                </p>
                            </div>
                        </div>
                        <div class="col-right">
                            <template v-if="item.article.totalPrice >= 25000">
                                <span class="text-xl font-bold">Preis auf Anfrage</span>
                            </template>
                            <div
                                v-else-if="item.article.totalPrice > 0"
                                class="text-xl font-bold"
                            >
                                <template v-if="StringTools.showPrice(item.article)">
                                    ab {{
                                        StringTools.currencyString(item.article.totalPrice)
                                    }}{{ item.article.unit ? ' / ' + item.article.unit : '' }}<br>
                                    <span class="text-sm font-normal">netto ab Werk</span>
                                </template>
                            </div>
                            <div
                                v-else
                                class="text-xl font-bold"
                            >
                                Preis auf Anfrage
                            </div>
                            <div class="flex items-center h-8 justify-end">
                                <UInputNumber
                                    :model-value="item.amount"
                                    :min="1"
                                    :max="10"
                                    @update:model-value="(v) => setAmount(item, v)"
                                />
                            </div>
                            <div class="space-x-8">
                                <NuxtLink
                                    :to="{
                                    name: 'product',
                                    params: {
                                        id: item.article._id,
                                        category: StringTools.simplify(item.article.categories?.[0]?.name ?? ''),
                                        name: StringTools.simplify(item.article.name + ' ' + item.article.name2),
                                    },
                                }"
                                >
                                    Zur Artikeldetailseite
                                </NuxtLink>
                                <a
                                    v-if="hasSpecSheet(item)"
                                    class="cursor-pointer"
                                    @click.prevent="downloadSpecSheet(item.article)"
                                >Datenblatt herunterladen</a>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="py-4 lg:py-8">
                    <hr>
                </div>
                <div class="lg:grid lg:grid-cols-2 space-y-4 lg:space-y-0 justify-between">
                    <div class="w-full">
                        <button
                            v-if="items.length > 0"
                            class="bordered flex! gap-2 mx-auto lg:mx-0"
                            @click="downloadWishlist()"
                        >
                            <IconPDF class="w-4 h-4 fill-secondary"/>
                            Merkzettel herunterladen
                        </button>
                        <button
                            v-if="items.length > 0"
                            class="bordered flex! gap-2 mx-auto lg:mx-0 mt-4"
                            @click="openModalOffer = true"
                        >
                            <IconPaperPlane class="w-4 h-4 fill-secondary"/>
                            Unverbindliches Angebot anfragen
                        </button>
                        <UModal
                            v-model:open="openModalOffer"
                            :close="{
                              color: 'secondary',
                              variant: 'solid',
                              class: 'rounded-full'
                            }"
                            title="Unverbindliches Angebot anfragen"
                            :content="{
                              class: '2xl:!max-w-3xl'
                            }"
                        >
                            <template #body>
                                <ModalOffer @close="openModalOffer = false"/>
                            </template>
                        </UModal>
                    </div>
                    <div class="space-y-4 lg:flex lg:flex-col lg:justify-end">
                        <div class="place-self-end w-full grid grid-cols-2 2xl:w-1/2">
                            <div class="text-xl font-bold">
                                Gesamtsumme
                            </div>
                            <div class="flex flex-col text-xl font-bold text-right">
                                <span>{{ sum(false) }}</span>
                            </div>
                        </div>
                        <p class="place-self-end text-right">
                            Alle Preise netto ab Werk zzgl. Materialkostenzuschlag
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </main>
</template>

<script setup lang="ts">
import {StringTools} from '~~/utils/StringTools';
import IconClose from '~/components/Icons/IconClose.vue';
import IconPDF from '~/components/Icons/IconPDF.vue';
import IconPaperPlane from '~/components/Icons/IconPaperPlane.vue';
import {useSpecSheetStore} from '~~/stores/specsheet';
import ModalOffer from '~/components/Modal/Offer.vue';
import {useWishlistStore} from '~~/stores/wishlist';

definePageMeta({
    path: '/mein-merkzettel',
});

const wishlistStore = useWishlistStore();
const items = ref([]);
const isLoading = ref(true);

onMounted(async () => {
    await wishlistStore.init();
    // todo: specsheetstore check
    items.value = wishlistStore.items;
    isLoading.value = false;
});

const openModalOffer = ref(false);

async function removeItem(item: any) {
    item.amount = Number(item.amount);
    await wishlistStore.delete(item);
}

async function setAmount(item: any, amount: number) {
    item.amount = amount;
    await wishlistStore.update(item);
}

/**
 * PDF-Datenblatt vorhanden?
 */
const specSheetStore = useSpecSheetStore();
const specSheet = reactive<Record<string, boolean>>({});

watchEffect(async () => {
    for (const it of items.value) {
        const nr = it?.article?.articleNumber;
        if (!nr || nr in specSheet) continue;
        specSheet[nr] = await specSheetStore.check(it.article);
    }
});

function hasSpecSheet(item: any) {
    const nr = item?.article?.articleNumber;
    return nr ? !!specSheet[nr] : false;
}

async function downloadSpecSheet(product: any) {
    if (!product) return;
    await specSheetStore.download(product);
}

const totalNet = computed(() => {
    let total = 0;
    let highPrice = false;

    for (const item of items.value) {
        if (item.article.totalPrice >= 25000) highPrice = true;
        if (StringTools.showPrice(item.article)) {
            total += item.article.totalPrice * item.amount;
        }
    }

    return {
        total,
        highPrice,
    };
});

function sum(withVat: boolean) {
    const {
        total,
        highPrice,
    } = totalNet.value;

    if (highPrice) return 'Preis auf Anfrage';

    const value = withVat ? total * 1.19 : total;
    return 'ab ' + StringTools.currencyString(value);
}

async function downloadWishlist() {
    const {public: pub} = useRuntimeConfig();
    const auth = useAuth?.(); // falls du ein Auth-Composable hast; sonst unten anpassen

    const data: any = {};
    if (wishlistStore.userData) {
        data.userData = wishlistStore.userData;
    }

    const url = `${pub.apiUrl}${wishlistStore.wishlistId}/export`;

    // raw => damit du an Headers kommst
    const res = await $fetch.raw<ArrayBuffer>(url, {
        method: 'POST',
        body: data,
        responseType: 'arrayBuffer',
    });

    // Header lesen (kann je nach Backend case/format variieren)
    const cd = res.headers.get('content-disposition') || '';
    const filename = getFilenameFromContentDisposition(cd) || 'wishlist.pdf';

    // PDF Blob + Download
    const blob = new Blob([res._data], {type: 'application/pdf'});
    const objectUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(objectUrl);
}

function getFilenameFromContentDisposition(cd: string): string | null {
    // RFC 5987: filename*=UTF-8''...
    const mStar = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (mStar?.[1]) return decodeURIComponent(mStar[1].replace(/(^"|"$)/g, ''));

    // fallback: filename="..."
    const m = cd.match(/filename\s*=\s*"?([^"]+)"?/i);
    if (m?.[1]) return m[1];

    return null;
}

</script>
